/**
 * Member role model — multi-role via comma-separated string in `member.role`.
 *
 * Better Auth's organization plugin stores `member.role` as freeform text; we
 * use a CSV idiom to let a single member hold both an authority role
 * (`owner | admin | member`) and the orthogonal `instructor` flag.
 *
 * `composeRole` is the only sanctioned writer — it canonicalises order so
 * downstream SQL predicates (e.g., counting owners, listing org instructors)
 * can rely on a predictable layout.
 */

export const AUTHORITY_ROLES = ['owner', 'admin', 'member'] as const
export type AuthorityRole = (typeof AUTHORITY_ROLES)[number]

export const MEMBER_ROLES = [...AUTHORITY_ROLES, 'instructor'] as const
export type MemberRole = (typeof MEMBER_ROLES)[number]

const AUTHORITY_RANK: Record<AuthorityRole, number> = {
  owner: 0,
  admin: 1,
  member: 2,
}

const MEMBER_ROLE_SET = new Set<string>(MEMBER_ROLES)
const AUTHORITY_ROLE_SET = new Set<string>(AUTHORITY_ROLES)

export function isMemberRole(value: string): value is MemberRole {
  return MEMBER_ROLE_SET.has(value)
}

export function isAuthorityRole(value: string): value is AuthorityRole {
  return AUTHORITY_ROLE_SET.has(value)
}

/** Split a stored role string into its component roles, ignoring unknowns. */
export function splitRoles(raw: string | null | undefined): MemberRole[] {
  if (!raw)
    return []
  const seen = new Set<MemberRole>()
  for (const part of raw.split(',')) {
    const trimmed = part.trim()
    if (isMemberRole(trimmed))
      seen.add(trimmed)
  }
  return [...seen]
}

export function hasRole(raw: string | null | undefined, role: MemberRole): boolean {
  return splitRoles(raw).includes(role)
}

/**
 * Parse a stored role string into its authority + instructor flag form. If
 * multiple authority roles are present, the highest-rank one wins. Defaults
 * to `member` when no authority role is found.
 */
export function parseRole(raw: string | null | undefined): {
  authority: AuthorityRole
  isInstructor: boolean
} {
  const roles = splitRoles(raw)
  const authorities = roles.filter(isAuthorityRole)
  authorities.sort((a, b) => AUTHORITY_RANK[a] - AUTHORITY_RANK[b])
  const authority = authorities[0] ?? 'member'
  return { authority, isInstructor: roles.includes('instructor') }
}

/** Canonicalised role string. Authority first, then optional `,instructor`. */
export function composeRole(input: {
  authority: AuthorityRole
  isInstructor: boolean
}): string {
  return input.isInstructor ? `${input.authority},instructor` : input.authority
}
